import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';

import {
    type GetVisualizationPropsArgs,
    type GetVisualizationPropsResponse,
    VisualizationErrorVersion,
} from '../models';

import { counterWorkflow, createCounterArgsSchema } from './counter';
import { createPieArgsSchema, pieWorkflow } from './pie';
import { createSankeyArgsSchema, sankeyWorkflow } from './sankey';
import { createTableArgsSchema, tableWorkflow } from './table';
import { createTimeseriesArgsSchema, timeseriesWorkflow } from './timeseries';
import { createProcessor } from './validation';

// Create the visualization system registry with workflow support using type-guided builder
export const visualizationWorkflows = {
    [StudioVisualizationType.Timeseries]: createProcessor()
        .schema(createTimeseriesArgsSchema)
        .workflow(timeseriesWorkflow)
        .build(),
    [StudioVisualizationType.Table]: createProcessor().schema(createTableArgsSchema).workflow(tableWorkflow).build(),
    [StudioVisualizationType.Counter]: createProcessor()
        .schema(createCounterArgsSchema)
        .workflow(counterWorkflow)
        .build(),
    [StudioVisualizationType.Pie]: createProcessor().schema(createPieArgsSchema).workflow(pieWorkflow).build(),
    [StudioVisualizationType.Sankey]: createProcessor().schema(createSankeyArgsSchema).workflow(sankeyWorkflow).build(),
};

export const getVisualizationPropsEnhanced = async <T extends StudioVisualizationType = StudioVisualizationType>(
    args: GetVisualizationPropsArgs,
): Promise<GetVisualizationPropsResponse<T>> => {
    try {
        if (!(args.type in visualizationWorkflows)) {
            throw new Error(`Invalid visualization type: ${args.type}`);
        }
        const result = await visualizationWorkflows[args.type](args);

        if (!result.success) {
            throw new Error(
                JSON.stringify({
                    version: VisualizationErrorVersion.V2,
                    errors: result.errors,
                }),
            );
        }

        return result.data as GetVisualizationPropsResponse<T>;
    } catch (error) {
        console.error('Error in getVisualizationPropsEnhanced:', error);
        throw error;
    }
};
